import React, { useState, useEffect } from 'react';
import {
  Box, Flex, Text, Grid, Input, Button, VStack, HStack, Link, Select,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useApp } from '../hooks/useApp';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const CATEGORIES = ['All', 'Smartphones', 'Laptops', 'Audio', 'Smartwatches', 'Cameras', 'AI Tools', 'General'];

export default function DocumentsPage() {
  const { documents } = useApp();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [copied, setCopied] = useState(null);

  // Scroll to hash on load (if someone lands via direct doc link)
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, []);

  const filtered = documents.filter(doc => {
    const matchSearch = !search ||
      doc.title?.toLowerCase().includes(search.toLowerCase()) ||
      doc.description?.toLowerCase().includes(search.toLowerCase()) ||
      doc.videoTitle?.toLowerCase().includes(search.toLowerCase());
    const matchCat = category === 'All' || doc.category === category;
    return matchSearch && matchCat;
  });

  const copyLink = (id) => {
    const url = `${process.env.REACT_APP_SITE_URL || window.location.origin}/docs#${id}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  return (
    <Box pt="68px" minH="100vh" bg="#07070a">
      {/* Hero Banner */}
      <Box
        position="relative"
        overflow="hidden"
        pt={{ base: 14, md: 20 }}
        pb={{ base: 12, md: 16 }}
        px="5%"
      >
        {/* Background orbs */}
        <Box
          position="absolute"
          w="500px"
          h="500px"
          bg="rgba(0,195,255,0.06)"
          borderRadius="50%"
          filter="blur(100px)"
          top="-150px"
          right="-100px"
          pointerEvents="none"
        />
        <Box
          position="absolute"
          w="300px"
          h="300px"
          bg="rgba(0,119,255,0.05)"
          borderRadius="50%"
          filter="blur(80px)"
          bottom="-80px"
          left="-50px"
          pointerEvents="none"
        />

        <Box maxW="800px" animation={`${fadeUp} 0.6s ease`}>
          {/* Banner notice */}
          <Box
            display="inline-flex"
            alignItems="center"
            gap={2}
            bg="rgba(0,195,255,0.1)"
            border="1px solid rgba(0,195,255,0.25)"
            borderRadius="full"
            px={4}
            py={1.5}
            mb={5}
          >
            <Box w="6px" h="6px" bg="#00c3ff" borderRadius="50%" />
            <Text fontSize="0.78rem" color="#00c3ff" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase">
              YouTube Resources Hub
            </Text>
          </Box>

          <Text
            fontFamily="'Fraunces', serif"
            fontSize={{ base: '2.2rem', md: '3rem' }}
            fontWeight={900}
            lineHeight={1.1}
            mb={4}
          >
            All Documents &{' '}
            <Text as="span" color="#00c3ff">
              Resources
            </Text>
          </Text>

          {/* The YouTube message */}
          <Box
            bg="rgba(0,195,255,0.06)"
            border="1px solid rgba(0,195,255,0.2)"
            borderRadius="12px"
            p={4}
            mb={6}
          >
            <Flex align="flex-start" gap={3}>
              <Text fontSize="1.3rem" flexShrink={0}>📌</Text>
              <Box>
                <Text fontWeight={600} fontSize="0.95rem" mb={1}>From TechBheekaran YouTube Channel</Text>
                <Text color="#6b7fa0" fontSize="0.88rem" lineHeight={1.7}>
                  In our YouTube videos, we mention — "all documents presented in this video, all downloadable document links are in the description."
                  That description link points here. Find and download your document below by searching the video title.
                </Text>
              </Box>
            </Flex>
          </Box>

          <Text color="#6b7fa0" fontSize="0.95rem" lineHeight={1.7}>
            All PDFs, guides, comparison sheets, and reference documents from our YouTube videos are available here. Search by video title or category to find your document.
          </Text>
        </Box>
      </Box>

      {/* Filters */}
      <Box
        px="5%"
        pb={4}
        borderBottom="1px solid rgba(0,195,255,0.08)"
        bg="rgba(10,15,26,0.8)"
        backdropFilter="blur(10px)"
        position="sticky"
        top="68px"
        zIndex={10}
        pt={4}
      >
        <Flex gap={3} maxW="1400px" mx="auto" direction={{ base: 'column', sm: 'row' }}>
          <Input
            placeholder="🔍  Search documents or video title..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            bg="#0e1520"
            border="1px solid rgba(0,195,255,0.15)"
            color="#f0f0f5"
            borderRadius="10px"
            _placeholder={{ color: '#6b7fa0' }}
            _focus={{ borderColor: '#00c3ff', boxShadow: '0 0 0 1px rgba(0,195,255,0.4)' }}
            flex={1}
          />
          <Select
            value={category}
            onChange={e => setCategory(e.target.value)}
            bg="#0e1520"
            border="1px solid rgba(0,195,255,0.15)"
            color="#f0f0f5"
            borderRadius="10px"
            w={{ base: '100%', sm: '180px' }}
            flexShrink={0}
            _focus={{ borderColor: '#00c3ff' }}
          >
            {CATEGORIES.map(c => (
              <option key={c} value={c} style={{ background: '#0e1520' }}>{c}</option>
            ))}
          </Select>
        </Flex>
      </Box>

      {/* Document Grid */}
      <Box px="5%" py={10} maxW="1400px" mx="auto">
        {filtered.length === 0 ? (
          <VStack py={20} gap={4} animation={`${fadeUp} 0.4s ease`}>
            <Text fontSize="3rem">📂</Text>
            <Text fontFamily="'Fraunces', serif" fontSize="1.3rem" fontWeight={700}>
              {documents.length === 0 ? 'No documents yet' : 'No matches found'}
            </Text>
            <Text color="#6b7fa0" fontSize="0.9rem" textAlign="center" maxW="400px">
              {documents.length === 0
                ? 'Documents from YouTube videos will appear here once the admin uploads them.'
                : 'Try a different search term or category.'}
            </Text>
            {search && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setSearch(''); setCategory('All'); }}
                color="#00c3ff"
                border="1px solid rgba(0,195,255,0.25)"
              >
                Clear filters
              </Button>
            )}
          </VStack>
        ) : (
          <>
            <Text color="#6b7fa0" fontSize="0.85rem" mb={6}>
              {filtered.length} document{filtered.length !== 1 ? 's' : ''} found
            </Text>
            <Grid
              templateColumns={{ base: '1fr', md: 'repeat(2,1fr)', lg: 'repeat(3,1fr)' }}
              gap={5}
            >
              {filtered.map((doc, i) => (
                <DocumentCard
                  key={doc.id}
                  doc={doc}
                  copied={copied}
                  onCopy={copyLink}
                  delay={i * 0.05}
                />
              ))}
            </Grid>
          </>
        )}
      </Box>
    </Box>
  );
}

function DocumentCard({ doc, copied, onCopy, delay }) {
  const categoryColors = {
    Smartphones: '#00c3ff',
    Laptops: '#0077ff',
    Audio: '#a855f7',
    Smartwatches: '#22d3ee',
    Cameras: '#f59e0b',
    'AI Tools': '#10b981',
    General: '#6b7fa0',
  };
  const accentColor = categoryColors[doc.category] || '#00c3ff';

  return (
    <Box
      id={doc.id}
      bg="#0a0f1a"
      border="1px solid rgba(0,195,255,0.1)"
      borderRadius="16px"
      overflow="hidden"
      transition="all 0.3s"
      animation={`${fadeUp} 0.5s ease ${delay}s both`}
      _hover={{
        transform: 'translateY(-4px)',
        borderColor: 'rgba(0,195,255,0.35)',
        boxShadow: '0 12px 40px rgba(0,195,255,0.1)',
      }}
    >
      {/* Color accent strip */}
      <Box h="3px" bgGradient={`linear(to-r, ${accentColor}, transparent)`} />

      <Box p={5}>
        {/* Header */}
        <Flex align="flex-start" justify="space-between" gap={3} mb={3}>
          <Box
            w="44px"
            h="44px"
            bg={`${accentColor}18`}
            border={`1px solid ${accentColor}30`}
            borderRadius="10px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontSize="1.3rem"
            flexShrink={0}
          >
            {doc.emoji || '📄'}
          </Box>
          {doc.category && (
            <Box
              px={2.5}
              py={0.5}
              bg={`${accentColor}18`}
              border={`1px solid ${accentColor}30`}
              borderRadius="full"
              fontSize="0.68rem"
              fontWeight={600}
              color={accentColor}
              textTransform="uppercase"
              letterSpacing="0.06em"
              flexShrink={0}
            >
              {doc.category}
            </Box>
          )}
        </Flex>

        {/* Title */}
        <Text
          fontFamily="'Fraunces', serif"
          fontWeight={700}
          fontSize="1rem"
          lineHeight={1.4}
          mb={2}
          noOfLines={2}
        >
          {doc.title}
        </Text>

        {/* Description */}
        {doc.description && (
          <Text color="#6b7fa0" fontSize="0.83rem" lineHeight={1.6} mb={3} noOfLines={2}>
            {doc.description}
          </Text>
        )}

        {/* Video reference */}
        {doc.videoTitle && (
          <Flex align="center" gap={2} mb={4}>
            <Box w="6px" h="6px" bg="#ec1a1a" borderRadius="50%" flexShrink={0} />
            <Text color="#6b7fa0" fontSize="0.78rem" noOfLines={1}>
              From: <Text as="span" color="#f0f0f5" fontWeight={500}>{doc.videoTitle}</Text>
            </Text>
          </Flex>
        )}

        {/* Actions */}
        <Flex gap={2} mt="auto">
          {doc.fileUrl ? (
            <Link
              href={doc.fileUrl}
              target="_blank"
              flex={1}
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap={1.5}
              bg="#00c3ff"
              color="#000"
              fontWeight={700}
              fontSize="0.82rem"
              py={2}
              borderRadius="8px"
              transition="all 0.2s"
              _hover={{ bg: '#33d0ff', textDecoration: 'none', boxShadow: '0 6px 20px rgba(0,195,255,0.35)' }}
            >
              ⬇ Download
            </Link>
          ) : (
            <Box
              flex={1}
              display="flex"
              alignItems="center"
              justifyContent="center"
              bg="rgba(255,255,255,0.05)"
              color="#6b7fa0"
              fontWeight={600}
              fontSize="0.82rem"
              py={2}
              borderRadius="8px"
            >
              Coming soon
            </Box>
          )}
          <Button
            onClick={() => onCopy(doc.id)}
            bg={copied === doc.id ? 'rgba(0,195,255,0.15)' : 'rgba(255,255,255,0.05)'}
            color={copied === doc.id ? '#00c3ff' : '#6b7fa0'}
            border="1px solid"
            borderColor={copied === doc.id ? 'rgba(0,195,255,0.4)' : 'rgba(255,255,255,0.1)'}
            fontWeight={600}
            fontSize="0.78rem"
            px={3}
            py={2}
            borderRadius="8px"
            transition="all 0.2s"
            _hover={{ bg: 'rgba(0,195,255,0.1)', color: '#00c3ff', borderColor: 'rgba(0,195,255,0.3)' }}
            flexShrink={0}
          >
            {copied === doc.id ? '✓ Copied!' : '🔗 Copy Link'}
          </Button>
        </Flex>
      </Box>
    </Box>
  );
}
